import { CloudCog } from "lucide-react";
import React, { useState, useEffect } from "react";
import Select, { Creatable } from "react-select";
import CreatableSelect from "react-select/creatable";

const BusinessModal = ({
  isOpen,
  onClose,
  business,
  onSave,
  isLoading,
  industries,
}) => {
  const [formData, setFormData] = useState({
    timestamp: "",
    email: "",
    its_number: "",
    name: "",
    contact: "",
    qualification: "",
    occupation: "",
    industry: "",
    designation: "",
    company_name: "",
    linkedin: "",
    website: "",
    logo: "",
    sponsored: false,
    level2_sponsored: false,
    internship: false,
    other_service: "",
    description: "",
    badges: [],
  });

  const [badgeInput, setBadgeInput] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    basicInfo: true,
    professionalInfo: false,
    onlinePresence: false,
    servicesStatus: false,
    badges: false,
  });

  const occupationOptions = [
    { value: "Business", label: "Business" },
    { value: "Professional", label: "Professional" },
  ];

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  useEffect(() => {
    setValidationErrors({});
  }, []);

  useEffect(() => {
    if (business) {
      setFormData({
        timestamp: business.timestamp || "",
        email: business.email || "",
        its_number: business.its_number || "",
        name: business.name || "",
        contact: business.contact || "",
        qualification: business.qualification || "",
        occupation: business.occupation || "",
        industry: business.industry || "",
        designation: business.designation || "",
        company_name: business.company_name || "",
        linkedin: business.linkedin || "",
        website: business.website || "",
        sponsored: business.sponsored || false,
        level2_sponsored: business.level2_sponsored || false,
        internship: business.internship || false,
        other_service: business.other_service || "",
        description: business.description || "",
        logo: business.logo || "",
        badges: business.badges || [],
      });
    } else {
      // For new business, set current timestamp
      setFormData({
        timestamp: new Date().toISOString(),
        email: "",
        its_number: "",
        name: "",
        contact: "",
        qualification: "",
        occupation: "",
        industry: "",
        designation: "",
        company_name: "",
        linkedin: "",
        website: "",
        sponsored: false,
        level2_sponsored: false,
        internship: false,
        other_service: "",
        description: "",
        logo: "",
        badges: [],
      });
    }
  }, [business]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    console.log(file);

    if (!file) return;

    // if (file.size > 2 * 1024 * 1024) { // 2MB limit
    //   alert('Image size should be less than 2MB');
    //   return;
    // }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({
        ...prev,
        logo: reader.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Clear previous error
    setValidationErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    // Email validation
    if (name === "email" && newValue) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newValue)) {
        setValidationErrors((prev) => ({
          ...prev,
          email: "Please enter a valid email address",
        }));
      }
    }

    // ITS Number validation (assuming it should be a positive number)
    if (name === "its_number" && newValue) {
      const itsNumber = parseInt(newValue, 10);
      if (isNaN(itsNumber) || itsNumber <= 0) {
        setValidationErrors((prev) => ({
          ...prev,
          its_number: "ITS Number must be a valid positive number",
        }));
      }
    }
  };

  const handleSelectChange = (selectedOption, { name }) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleAddBadge = () => {
    if (badgeInput.trim() && !formData.badges.includes(badgeInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        badges: [...prev.badges, badgeInput.trim()],
      }));
      setBadgeInput("");
    }
  };

  const handleRemoveBadge = (index) => {
    setFormData((prev) => ({
      ...prev,
      badges: prev.badges.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // First, validate all required fields
    const newValidationErrors = {};
    const requiredFields = [
      "email",
      "its_number",
      "name",
      "qualification",
      "occupation",
      "industry",
      "designation",
      "company_name",
      "contact",
    ];
    requiredFields.forEach((field) => {
      if (!formData[field]?.toString().trim()) {
        newValidationErrors[field] = `${field
          .replace("_", " ")
          .replace(/^\w/, (c) => c.toUpperCase())} is required`;
      }
    });

    // Email format validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newValidationErrors.email = "Please enter a valid email address";
    }

    // ITS Number validation
    if (formData.its_number) {
      const itsNumber = parseInt(formData.its_number, 10);
      if (isNaN(itsNumber) || itsNumber <= 0) {
        newValidationErrors.its_number =
          "ITS Number must be a valid positive number";
      }
    }

    // Update validation errors state
    setValidationErrors(newValidationErrors);

    // If no validation errors, submit the form

    if (
      !Object.keys(newValidationErrors).length &&
      !Object.values(newValidationErrors).some((error) => error)
    ) {
      await onSave(formData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-start justify-center p-2 sm:p-4">
      <div className="relative w-full max-w-6xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto shadow-lg rounded-md bg-white mt-2 sm:mt-8">
        <div className="sticky top-0 bg-white z-10 flex justify-between items-center p-4 sm:p-5 border-b border-gray-200 mb-0">
          <h3 className="text-xl font-semibold text-gray-900">
            {business ? "Edit Business" : "Create New Business"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-5 space-y-4 sm:space-y-6">
          {/* Basic Information Section */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={() => toggleSection("basicInfo")}
            >
              <h4 className="text-lg font-medium text-gray-900">
                Basic Information
              </h4>
              <svg
                className={`w-5 h-5 text-gray-500 transform transition-transform ${
                  expandedSections.basicInfo ? "rotate-180" : ""
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
            {expandedSections.basicInfo && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Timestamp
                  </label>
                  <input
                    type="datetime-local"
                    name="timestamp"
                    value={
                      formData.timestamp
                        ? new Date(formData.timestamp)
                            .toISOString()
                            .slice(0, 16)
                        : ""
                    }
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        timestamp: new Date(e.target.value).toISOString(),
                      }))
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <span className="text-red-500">{validationErrors.email}</span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    ITS Number *
                  </label>
                  <input
                    type="number"
                    name="its_number"
                    value={formData.its_number}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <span className="text-red-500">
                    {validationErrors.its_number}
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <span className="text-red-500">{validationErrors.name}</span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Contact
                  </label>
                  <input
                    type="tel"
                    name="contact"
                    value={formData.contact}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <span className="text-red-500">
                    {validationErrors.contact}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Professional Information Section */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={() => toggleSection("professionalInfo")}
            >
              <h4 className="text-lg font-medium text-gray-900">
                Professional Information
              </h4>
              <svg
                className={`w-5 h-5 text-gray-500 transform transition-transform ${
                  expandedSections.professionalInfo ? "rotate-180" : ""
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
            {expandedSections.professionalInfo && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Qualification *
                  </label>
                  <textarea
                    name="qualification"
                    value={formData.qualification}
                    onChange={handleInputChange}
                    rows={2}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <span className="text-red-500">
                    {validationErrors.qualification}
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Type *
                  </label>
                  <Select
                    name="occupation"
                    value={
                      formData.occupation === "Business"
                        ? occupationOptions[0]
                        : occupationOptions[1]
                    }
                    onChange={handleSelectChange}
                    options={occupationOptions}
                    className="mt-1"
                    classNamePrefix="select"
                    placeholder="Select occupation..."
                    isClearable
                  />
                  <span className="text-red-500">
                    {validationErrors.occupation}
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Industry *
                  </label>
                  <CreatableSelect
                    name="industry"
                    value={
                      formData.industry
                        ? {
                            value: formData.industry,
                            label: formData.industry,
                          }
                        : null
                    }
                    onChange={(selectedOption) => {
                      setFormData((prev) => ({
                        ...prev,
                        industry: selectedOption ? selectedOption.value : "",
                      }));
                    }}
                    options={
                      industries?.map((industry) => ({
                        value: industry,
                        label: industry,
                      })) || []
                    }
                    className="mt-1"
                    classNamePrefix="select"
                    placeholder="Select or add industry..."
                    isClearable
                    isLoading={!industries}
                    formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                  />
                  <span className="text-red-500">
                    {validationErrors.industry}
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Designation *
                  </label>
                  <input
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <span className="text-red-500">
                    {validationErrors.designation}
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <span className="text-red-500">
                    {validationErrors.company_name}
                  </span>
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Company Logo
                  </label>
                  <div className="mt-1 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex justify-center sm:justify-start">
                      {formData.logo ? (
                        <div className="w-24 h-24 flex items-center justify-center">
                          <img
                            src={formData.logo}
                            alt="Company logo"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center border border-gray-200">
                          <svg
                            className="h-6 w-6 text-gray-400"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                        </div>
                      )}
                      
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 items-center">
                      <label className="cursor-pointer">
                      <span className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                        Change
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={handleImageUpload}
                      />
                      </label>
                      {formData.logo && (
                        <button
                          type="button"
                          onClick={() =>
                            setFormData((prev) => ({ ...prev, logo: "" }))
                          }
                          className="text-sm text-red-600 hover:text-red-800 px-3 py-1 border border-red-300 rounded hover:bg-red-50"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Online Presence Section */}
          <div className="bg-green-50 p-4 rounded-lg">
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={() => toggleSection("onlinePresence")}
            >
              <h4 className="text-lg font-medium text-gray-900">
                Online Presence
              </h4>
              <svg
                className={`w-5 h-5 text-gray-500 transform transition-transform ${
                  expandedSections.onlinePresence ? "rotate-180" : ""
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
            {expandedSections.onlinePresence && (
              <div className="mt-4 grid grid-cols-1 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    LinkedIn
                  </label>
                  <textarea
                    name="linkedin"
                    value={formData.linkedin}
                    onChange={handleInputChange}
                    rows={2}
                    placeholder="https://linkedin.com/in/username"
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Website
                  </label>
                  <textarea
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    rows={2}
                    placeholder="https://example.com"
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Services & Status Section */}
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={() => toggleSection("servicesStatus")}
            >
              <h4 className="text-lg font-medium text-gray-900">
                Services & Status
              </h4>
              <svg
                className={`w-5 h-5 text-gray-500 transform transition-transform ${
                  expandedSections.servicesStatus ? "rotate-180" : ""
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
            {expandedSections.servicesStatus && (
              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Other Services
                  </label>
                  <textarea
                    name="other_service"
                    value={formData.other_service}
                    onChange={handleInputChange}
                    rows={2}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="sponsored"
                      checked={formData.sponsored}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Sponsored
                    </span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="level2_sponsored"
                      checked={formData.level2_sponsored}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Level 2 Sponsored
                    </span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="internship"
                      checked={formData.internship}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Internship
                    </span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Badges Section */}
          <div className="bg-purple-50 p-4 rounded-lg">
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={() => toggleSection("badges")}
            >
              <h4 className="text-lg font-medium text-gray-900">Badges</h4>
              <svg
                className={`w-5 h-5 text-gray-500 transform transition-transform ${
                  expandedSections.badges ? "rotate-180" : ""
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
            {expandedSections.badges && (
              <div className="mt-4 space-y-4">
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={badgeInput}
                    onChange={(e) => setBadgeInput(e.target.value)}
                    placeholder="Enter badge name"
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 min-w-0"
                    onKeyPress={(e) =>
                      e.key === "Enter" &&
                      (e.preventDefault(), handleAddBadge())
                    }
                  />
                  <button
                    type="button"
                    onClick={handleAddBadge}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 whitespace-nowrap"
                  >
                    Add Badge
                  </button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {formData.badges.map((badge, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800"
                    >
                      {badge}
                      <button
                        type="button"
                        onClick={() => handleRemoveBadge(index)}
                        className="ml-2 text-indigo-600 hover:text-indigo-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex flex-col sm:flex-row justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 order-2 sm:order-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                isLoading ||
                Object.values(validationErrors).some((error) => error)
              }
              className="w-full sm:w-auto px-6 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 order-1 sm:order-2"
            >
              {isLoading
                ? "Saving..."
                : business
                ? "Update Business"
                : "Create Business"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusinessModal;
